/* global localStorage */

import User from 'services/user'
import * as MutationTypes from 'store/mutation-types'
import VueCookies from 'vue-cookies'

// init state
const state = {
  user: VueCookies.get('user') ? VueCookies.get('user') : null,
  groups: [],
  defaultUserSettings: {},
  localDefaultUserSettings: Object.freeze({
    standardConfigurations: {
      'Receive an email when a mission is shared in a group': true,
      'Dark mode': false,
      'Cesium offline': false
    },
    advancedConfigurations: {}
  }),
  options: {},
  bearer: null,
  giveMeATour: false,
  gt_OrbitTransferParam1: {
    isActive: false,
    firstMissionTourDone: false,
    secondMissionTourDone: false,
    thirdMissionTourDone: false,
    firstSpacecraftTourDone: false,
    secondSpacecraftTourDone: false,
    thrusterTourDone: false,
    firstSideNavTourDone: false,
    secondSideNavTourDone: false,
    orbitsTourDone: false
  },
  gt_OrbitTransferParam2: {
    isActive: false,
    firstMissionTourDone: false,
    firstPlotDone: false,
    secondPlotDone: false,
    thirdPlotDone: false
  },
  gt_PerformanceAnalysis: {
    isActive: false,
    firstGeometryDone: false,
    secondGeometryDone: false,
    firstPayloadDone: false,
    earthMeshDone: false
  }
}

const getters = {
  currentUser(state) {
    return state.user
  },
  groups(state) {
    return state.groups
  },
  userSettings(state) {
    return state.user?.userSettings
  },
  defaultUserSettings(state) {
    return state.defaultUserSettings
  },
  localDefaultUserSettings(state) {
    return state.localDefaultUserSettings
  },
  options(state) {
    return state.options
  },
  giveMeATour(state) {
    return state.giveMeATour
  },
  gt_OrbitTransferParam1(state) {
    return state.gt_OrbitTransferParam1
  },
  gt_OrbitTransferParam2(state) {
    return state.gt_OrbitTransferParam2
  },
  gt_PerformanceAnalysis(state) {
    return state.gt_PerformanceAnalysis
  }
}

const mutations = {
  [MutationTypes.LOGIN](state) {
    state.user = VueCookies.get('user')
  },
  [MutationTypes.LOGOUT](state) {
    state.user = null
  },
  [MutationTypes.UPDATE_USER_SETTINGS](state, userSettings) {
    state.user = { ...state.user, userSettings }
  },
  [MutationTypes.UPDATE_DEFAULT_SETTINGS](state, defaultUserSettings) {
    state.defaultUserSettings = defaultUserSettings
  },
  [MutationTypes.UPDATE_OPTIONS](state, options) {
    state.user = { ...state.user, options }
  },
  startATour(state, val) {
    state.giveMeATour = val
  },
  start_gt_OrbitTransferParam1(state, { param, val }) {
    state.gt_OrbitTransferParam1[param] = val
  },
  start_gt_OrbitTransferParam2(state, { param, val }) {
    state.gt_OrbitTransferParam2[param] = val
  },
  start_gt_PerformanceAnalysis(state, { param, val }) {
    state.gt_PerformanceAnalysis[param] = val
  }
}

const actions = {
  async login({ commit }, bearer) {
    // clean existing cookie
    await this._vm.$http.get('/users/logout').catch((e) => {
      console.warn('Logout failed', e)
    })
    // then call backend login from front to set backend secure cookie
    const userFromJwt = User.from(bearer)
    const axiosConfig = {
      headers: { Authorization: `Bearer ${bearer}` }
    }
    await this._vm.$http
      .get(`/users/${userFromJwt.id}/infos`, axiosConfig)
      .catch((e) => {
        console.warn('Login failed', e)
      })
    VueCookies.set(
      'user',
      JSON.stringify(userFromJwt),
      '1d',
      '/',
      '',
      true,
      'Lax'
    )
    commit(MutationTypes.LOGIN)
  },

  async logout({ commit }) {
    // call backend to remove jwt bearer set by java backend httponly
    await this._vm.$http.get('/users/logout').catch((e) => {
      console.warn('Logout failed', e)
    })
    VueCookies.remove('user')
    commit(MutationTypes.LOGOUT)
  },

  updateUserSettings({ commit }, userSettings) {
    commit(MutationTypes.UPDATE_USER_SETTINGS, userSettings)
  },

  updateDefaultSettings({ commit }, defaultUserSettings) {
    commit(MutationTypes.UPDATE_DEFAULT_SETTINGS, defaultUserSettings)
  },

  updateOptions({ commit }, options) {
    commit(MutationTypes.UPDATE_OPTIONS, options)
  },

  async loadUserGroups({ commit }) {
    if (!state.user) {
      state.groups = []
      return
    }
    this._vm.$http
      .get('/groups/' + state.user.id + '/usergroups')
      .then((response) => {
        state.groups = response.data
      })
      .catch((e) => {
        // debug purpose
        console.log('Error : could not retrieve groups : ', e)
        state.groups = []
      })
  },

  async loadUserSettings({ dispatch }) {
    await this._vm.$http
      .get(`/users/${state.user.id}/usersettings`)
      .then((userSettingsReq) =>
        dispatch('updateUserSettings', userSettingsReq.data)
      )
    await dispatch('loadDefaultSettings')
  },

  async loadDefaultSettings({ dispatch }) {
    await this._vm.$http
      .get(`/users/defaultsettings`)
      .then((response) => dispatch('updateDefaultSettings', response.data))
      .catch(() =>
        dispatch('updateDefaultSettings', state.localDefaultUserSettings)
      )
  },

  async loadOptions({ dispatch }) {
    if (state.user?.id == null) {
      return
    }
    await this._vm.$http
      .get(`/users/${state.user.id}/options`)
      .then((optionsReq) => dispatch('updateOptions', optionsReq.data))
  },

  startATour({ commit }, val) {
    commit('startATour', val)
  },
  start_gt_OrbitTransferParam1({ commit }, { param, val }) {
    commit('start_gt_OrbitTransferParam1', { param, val })
  },
  start_gt_OrbitTransferParam2({ commit }, { param, val }) {
    commit('start_gt_OrbitTransferParam2', { param, val })
  },
  start_gt_PerformanceAnalysis({ commit }, { param, val }) {
    commit('start_gt_PerformanceAnalysis', { param, val })
  }
}

export default {
  state,
  mutations,
  getters,
  actions
}
